
import { setLogLevel, onSnapshot, type Query, type QuerySnapshot } from 'firebase/firestore';

type RealtimeEntry = {
  id: number;
  label: string;
  path: string;
  openedAt: number;
  ticks: number;
  lastTickAt: number | null;
};

let nextId = 1;
const listeners = new Map<number, RealtimeEntry>();
const minuteWindow = new Map<string, number[]>();
let consoleTapOn = false;
let origDebug: (...args: any[]) => void;


function tick(label: string, path: string) {
  const key = `${label} | ${path}`;
  const now = Date.now();
  const arr = minuteWindow.get(key) ?? [];
  arr.push(now);

  while (arr.length && now - arr[0] > 60_000) arr.shift();
  minuteWindow.set(key, arr);
}


export function tracedOnSnapshot<T>(
  label: string,
  path: string,
  q: Query<T>,
  next: (snap: QuerySnapshot<T>) => void,
  error?: (e: Error) => void
): () => void {
  const id = nextId++;
  listeners.set(id, {
    id,
    label,
    path,
    openedAt: Date.now(),
    ticks: 0,
    lastTickAt: null,
  });
  console.info(`[FS][listen-open] #${id} ${label} -> ${path}`);

  const un = onSnapshot(
    q,
    (snap) => {
      const rec = listeners.get(id);
      if (rec) {
        rec.ticks += 1;
        rec.lastTickAt = Date.now();
      }
      tick(label, path);
      next(snap);
    },
    (err) => {
      console.warn(`[FS][listen-error] #${id} ${label} -> ${path}`, err);
      error?.(err as any);
    }
  );

  return () => {
    console.info(`[FS][listen-close] #${id} ${label} -> ${path}`);
    listeners.delete(id);
    un();
  };
}

function enableConsoleTap() {
  if (consoleTapOn) return;
  consoleTapOn = true;
  origDebug = console.debug;
  console.debug = (...args: any[]) => {
    try {
      const s = (args ?? []).join(' ');

      if (s.includes('Firestore') && /GetDocument|RunQuery|Listen/i.test(s)) {

        const m = s.match(/projects\/[^"]+\/documents\/([^\s,"']+)/);
        const path = m?.[1]?.replace(/^[^/]*\/(default)\//, '') ?? '(unknown-path)';
        const label = s.includes('GetDocument') ? 'getDoc'
                    : s.includes('RunQuery') ? 'getDocs'
                    : s.includes('Listen') ? 'listen'
                    : 'fs';
        tick(label, path);
      }
    } catch {}
    try { origDebug?.(...args); } catch {}
  };
}

function disableConsoleTap() {
  if (!consoleTapOn) return;
  consoleTapOn = false;
  console.debug = origDebug || console.debug;
}


export function installFirestoreDebug() {
  try { setLogLevel('debug'); } catch {}
  (window as any).fs = {

    dump() {
      const rows = Array.from(minuteWindow.entries())
        .map(([key, ts]) => ({ source: key, readsLast60s: ts.length }))
        .filter(r => r.readsLast60s > 0)
        .sort((a, b) => b.readsLast60s - a.readsLast60s);
      console.table(rows);
      if (rows.length === 0) console.log('No reads recorded in the last 60 seconds.');
      return rows;
    },

    listeners() {
      const rows = Array.from(listeners.values()).map(l => ({
        id: l.id,
        label: l.label,
        path: l.path,
        ticks: l.ticks,
        msOpen: Date.now() - l.openedAt,
        lastTickAgoMs: l.lastTickAt ? Date.now() - l.lastTickAt : null
      }));
      console.table(rows);
      return rows;
    },

    tapOn: enableConsoleTap,
    tapOff: disableConsoleTap,
  };
  console.info('[FS DEBUG] installed. Try: fs.dump(), fs.listeners(), fs.tapOn()');
}
